import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Trip, SignUpData, Apartment } from '@/types';
import { signUpService } from '@/services/signUpService';
import { apartmentService } from '@/services/apartmentService';
import { travellerService } from '@/services/travellerService';
import MemberStatusChip from '@/components/ui/MemberStatusChip';
import ConvertSignUpDialog from './ConvertSignUpDialog';
import ReusableDataGrid from '@/components/ui/ReusableDataGrid';
import { GridColDef, GridRowsProp } from '@mui/x-data-grid';
// Native MUI Components
import {
  Button,
  Typography,
  Box,
  Link,
  Chip,
  IconButton,
} from '@mui/material';
import { 
  Delete, 
  PersonAdd, 
  Email
} from '@mui/icons-material';
import { useToast } from '@/hooks/use-toast';
import { useI18n } from '@/hooks/useI18n';

interface PendingSignUpsProps {
  trip: Trip;
}

const PendingSignUps: React.FC<PendingSignUpsProps> = ({ trip }) => {
  const [pendingSignUps, setPendingSignUps] = useState<SignUpData[]>([]);
  const [apartments, setApartments] = useState<Apartment[]>([]);
  const [selectedSignUp, setSelectedSignUp] = useState<SignUpData | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { toast } = useToast();
  const { t } = useI18n();
  const unsubscribeApartmentsRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!trip.id) return;

    const unsubscribeSignUps = signUpService.listenToPendingSignUps(trip.id, (signups) => {
      setPendingSignUps(signups);
    });

    // Cleanup function for when the component unmounts
    return () => {
      unsubscribeSignUps();
    };
  }, [trip.id]);

  // Optional: Stop apartment listener when no pending sign-ups and dialog is closed
  useEffect(() => {
    if (pendingSignUps.length === 0 && !isDialogOpen && unsubscribeApartmentsRef.current) {
      // Optional: Uncomment to stop apartment listener when not needed
      // unsubscribeApartmentsRef.current();
      // unsubscribeApartmentsRef.current = null;
      // setApartments([]);
    }
  }, [pendingSignUps.length, isDialogOpen]);

  // Cleanup apartment listener on component unmount
  useEffect(() => {
    return () => {
      if (unsubscribeApartmentsRef.current) {
        unsubscribeApartmentsRef.current();
        unsubscribeApartmentsRef.current = null;
      }
    };
  }, []);

  const handleDeleteSignUp = useCallback(async (signUpId: string) => {
    if (!trip.id || !signUpId) return;
    try {
      await signUpService.deleteSignUp(trip.id, signUpId);
      setPendingSignUps(prev => prev.filter(s => s.id !== signUpId));
      toast({ 
        title: t('signup.pendingSignUps.signUpDeleted'), 
        description: t('signup.pendingSignUps.signUpDeletedDescription')
      });
    } catch (error) {
      console.error("Error deleting sign up: ", error);
      toast({ 
        title: t('signup.pendingSignUps.errorDeletingSignUp'), 
        description: t('signup.pendingSignUps.errorDeletingSignUpDescription'), 
        variant: "destructive" 
      });
    }
  }, [trip.id, toast, t]);

  const openConvertToTravellerDialog = useCallback((signUp: SignUpData) => {
    // Lazy load apartments: Start listener only when first dialog is opened
    if (!unsubscribeApartmentsRef.current && trip.id) {
      unsubscribeApartmentsRef.current = apartmentService.listenToApartments(trip.id, (loadedApartments) => {
        setApartments(loadedApartments);
      });
    }

    setSelectedSignUp(signUp);
    setIsDialogOpen(true);
  }, [trip.id]);

  const handleConfirm = async (apartmentId: string, apartmentTypeId: string | null, skiPassType: string | undefined, transportationType: string, isMember: boolean) => {
    if (!trip || !selectedSignUp) {
        toast({ 
          title: t('common.error'), 
          description: t('signup.pendingSignUps.missingInformation'), 
          variant: "destructive" 
        });
        return;
    }

    try {
      const newTraveller = await signUpService.confirmSignUp(
        trip,
        selectedSignUp,
        apartmentId,
        apartmentTypeId,
        skiPassType,
        transportationType,
        isMember
      );

      // Handle the traveller creation directly here instead of prop drilling
      if (trip.id) {
        if (newTraveller.id) {
          await travellerService.updateTraveller(trip.id, newTraveller.id, newTraveller);
        } else {
          await travellerService.addTraveller(trip.id, newTraveller);
        }
      }

      toast({
        title: t('signup.pendingSignUps.travellerAdded'),
        description: t('signup.pendingSignUps.travellerAddedDescription', { name: selectedSignUp.primaryParticipant.name }),
      });

    } catch (error) {
      console.error("Error confirming sign up: ", error);
      toast({ 
        title: t('common.error'), 
        description: t('signup.pendingSignUps.errorConfirmingSignUp'), 
        variant: "destructive" 
      });
    } finally {
      setIsDialogOpen(false);
      setSelectedSignUp(null);
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedSignUp(null);
  };

  // DataGrid columns definition
  const columns: GridColDef[] = useMemo(() => [
    {
      field: 'nameAndStatus',
      headerName: `👤 ${t('signup.pendingSignUps.nameAndStatus')}`,
      flex: 1,
      minWidth: 200,
      valueGetter: (value, row) => {
        // For sorting: Group members together, with primary first
        const groupId = row.groupSignUpId || row.id;
        const isPrimary = row.isPrimaryTraveller ? '0' : '1'; // 0 = primary sorts first
        return `${groupId}_${isPrimary}_${row.primaryParticipant.name}`;
      },
      sortComparator: (v1, v2) => {
        // Sort to group members together, with primary travelers first
        return v1.localeCompare(v2);
      },
      renderCell: (params) => {
        const signUp = params.row as SignUpData;
        const isCompanion = signUp.isGroupSignUp && !signUp.isPrimaryTraveller;
        
        return (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: 0.5, 
            py: 1,
            // Indent companions to show hierarchy
            pl: isCompanion ? 2 : 0,
            borderLeft: isCompanion ? '3px solid' : 'none',
            borderLeftColor: isCompanion ? 'primary.main' : 'transparent',
          }}>
            <Typography variant="body2" sx={{ 
              fontWeight: 500,
              color: isCompanion ? 'text.secondary' : 'text.primary'
            }}>
              {isCompanion && '↳ '}{signUp.primaryParticipant.name}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              {signUp.isGroupSignUp && (
                <Chip 
                  label={signUp.isPrimaryTraveller ? t('signup.pendingSignUps.primary') : t('signup.pendingSignUps.companion')} 
                  size="small" 
                  sx={{ fontSize: '0.75rem', height: 20 }}
                  color={signUp.isPrimaryTraveller ? "primary" : "default"}
                />
              )}
              <MemberStatusChip isMember={signUp.primaryParticipant.isSkiClubMember} />
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'email',
      headerName: `📧 ${t('common.email')}`,
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Link 
          href={`mailto:${params.value}`} 
          sx={{ 
            display: 'flex', 
            alignItems: 'center',
            color: 'primary.main',
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' }
          }}
        >
          <Email sx={{ mr: 0.5, width: 16, height: 16 }} />
          {params.value}
        </Link>
      ),
    },
    {
      field: 'phone',
      headerName: `📱 ${t('common.phone')}`,
      flex: 0.8,
      minWidth: 150,
    },
    {
      field: 'preferredApartmentType',
      headerName: `🏠 ${t('signup.pendingSignUps.preferredApartmentType')}`,
      flex: 1,
      minWidth: 200,
      valueGetter: (value, row) => {
        const preferredApartmentType = trip.apartmentTypes.find(at => at.id === row.preferredApartmentTypeId);
        return preferredApartmentType ? preferredApartmentType.name : t('common.na');
      },
    },
    {
      field: 'actions',
      headerName: t('common.actions'),
      flex: 1,
      minWidth: 200,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const signUp = params.row as SignUpData;
        return (
          <Box sx={{ display: 'flex', gap: 1, py: 1 }}>
            <Button
              variant="contained"
              size="small"
              onClick={() => openConvertToTravellerDialog(signUp)}
              startIcon={<PersonAdd />}
            >
              {t('signup.pendingSignUps.convert')}
            </Button>
            <IconButton 
              color="error" 
              size="small" 
              onClick={() => handleDeleteSignUp(signUp.id!)}
              sx={{ ml: 0.5 }}
            >
              <Delete />
            </IconButton>
          </Box>
        );
      },
    },
  ], [t, trip.apartmentTypes, openConvertToTravellerDialog, handleDeleteSignUp]);

  // DataGrid rows preparation
  const rows: GridRowsProp = useMemo(() => 
    pendingSignUps.map((signUp) => ({
      ...signUp,
      id: signUp.id || crypto.randomUUID(), // Ensure each row has an ID
    })),
    [pendingSignUps]
  );

  // Empty state component
  const EmptyState = () => (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      justifyContent: 'center',
      py: 8,
      px: 2
    }}>
      <Typography variant="h6" component="h3" sx={{ fontWeight: 'semibold', color: 'grey.700', mb: 1 }}>
        🎉 {t('signup.pendingSignUps.noPendingSignUps')}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {t('signup.pendingSignUps.noPendingSignUpsDescription')}
      </Typography>
    </Box>
  );

  return (
    <Box>
      {pendingSignUps.length === 0 ? (
        <EmptyState />
      ) : (
        <ReusableDataGrid
          rows={rows}
          columns={columns}
          pageSize={25}
          rowHeight={70}
          getRowId={(row) => row.id}
          initialState={{
            sorting: {
              sortModel: [{ field: 'nameAndStatus', sort: 'asc' }],
            },
          }}
        />
      )}

      <ConvertSignUpDialog
        open={isDialogOpen}
        signUp={selectedSignUp}
        trip={trip}
        apartments={apartments}
        onClose={handleCloseDialog}
        onConfirm={handleConfirm}
      />
    </Box>
  );
};

export default PendingSignUps;
